/*
  © 2024 KondaSoft
  https://www.kondasoft.com
*/

const formatMoneyWithoutTrailingZeros = (amount) => {
  if (window.theme?.formatMoneyWithoutTrailingZeros) {
    return window.theme.formatMoneyWithoutTrailingZeros(amount);
  }

  if (!window.Shopify || typeof window.Shopify.formatMoney !== 'function') {
    const fallback = Number(amount) / 100;
    if (Number.isNaN(fallback)) {
      return '';
    }
    const stringValue = fallback.toFixed(2);
    return stringValue.endsWith('.00') ? stringValue.slice(0, -3) : stringValue;
  }

  return window.Shopify.formatMoney(amount) || '';
};

class ProductForm extends HTMLElement {
  constructor () {
    super()

    this.form = this.querySelector('form')
    try {
      this.variants = JSON.parse(this.form.dataset.variants || '[]')
    } catch (error) {
      console.error('Failed to parse variants JSON', error)
      this.variants = []
    }
    this.productOptions = this.form.querySelectorAll('.product-options [name*="option"]')
    this.atcBtn = this.form.querySelector('.btn-atc')
    this.buyBtn = this.form.querySelector('.btn-buy')

    this.form.addEventListener('submit', event => this.onSubmit(event))

    this.productOptions.forEach(elem =>
      elem.addEventListener('change', event => this.onChangeProductOption(event)))

    this.buyBtn?.addEventListener('click', event => this.onClickBuyBtn(event))

    const initialVariantId = Number(this.form.querySelector('input[name="id"]')?.value)
    if (initialVariantId) {
      const initialVariant = this.variants.find(variant => Number(variant.id) === initialVariantId)
      if (initialVariant) {
        window.theme?.updateAfterpayProductPlacement?.(initialVariant.price, this)
      }
    }
  }

  async onChangeProductOption (event) {
    event.preventDefault()

    const selectedOptions = [...this.productOptions]
      .filter(el => el.type === 'select-one' || (el.type === 'radio' && el.checked))
      .map(el => el.value)

    const selectedVariant = this.variants.find(variant =>
      JSON.stringify(variant.options) === JSON.stringify(selectedOptions)
    )

    const previousVariantId = this.form.querySelector('input[name="id"]')?.value

    this.adjustPricing(selectedVariant)
    this.adjustButtons(selectedVariant)

    if (selectedVariant) {
      // Update the variant ID in the form
      const variantInput = this.form.querySelector('input[name="id"]')
      if (variantInput) {
        variantInput.value = selectedVariant.id
        variantInput.setAttribute('value', selectedVariant.id)
        variantInput.dispatchEvent(new Event('change', { bubbles: true }))
        
        // Also update the data-variant-id attribute
        variantInput.setAttribute('data-variant-id', selectedVariant.id)
      } else {
        console.error('Could not find variant input in form')
      }

      // Update URL with variant parameter
      if (this.closest('.product-main')) {
        const url = new URL(window.location)
        url.searchParams.set('variant', selectedVariant.id)
        window.history.replaceState({}, '', url)
      }

      // Update media gallery
      const mediaGallery = document.querySelector('product-media-gallery')
      if (mediaGallery) {
        const variantMedia = JSON.parse(mediaGallery.dataset.variantMedia || '{}')
        const mediaPosition = variantMedia[selectedVariant.id]
        
        if (mediaPosition !== undefined) {
          mediaGallery.dispatchEvent(new CustomEvent('variant:changed', {
            detail: { position: mediaGallery.getNativeMediaSlideIndex(mediaPosition) }
          }))
        }
      }

      if (event.isTrusted && this.closest('.product-main') && String(selectedVariant.id) !== String(previousVariantId)) {
        const handle = this.dataset.productHandle || window.location.pathname.match(/\/products\/([^/]+)/)?.[1]
        window.dispatchEvent(new CustomEvent('ws:track', { detail: {
          type: 'product_variant_selected', payload: {
            product_handle: handle, product_id: this.form.dataset.productid, variant_id: selectedVariant.id,
            variant_title: selectedVariant.title, options: selectedVariant.options
          }
        } }))
      }

      // Dispatch event for other components
      window.dispatchEvent(new CustomEvent('kt.product.variant.change', {
        detail: selectedVariant
      }))
    }
  }

  adjustPricing (selectedVariant) {
    if (!selectedVariant) {
      console.warn('No variant selected for pricing adjustment');
      return;
    }

    // Safely get all price elements with null checks
    const comparePrice = this.form?.querySelector('.product-price-compare');
    const finalPrice = this.form?.querySelector('.product-price-final');
    const compareBadge = this.form?.querySelector('.product-sale-badge');
    const soldOutBadge = this.form?.querySelector('.product-sold-out-badge');

    // --- Handle final price ---
    if (finalPrice) {
      // Ensure hidden span exists for accessibility
      let hiddenSpan = finalPrice.querySelector('.visually-hidden');
      if (!hiddenSpan) {
        hiddenSpan = document.createElement('span');
        hiddenSpan.className = 'visually-hidden';
        finalPrice.insertBefore(hiddenSpan, finalPrice.firstChild);
      }

      // Ensure we have a dedicated element for the price amount
      let priceAmount = finalPrice.querySelector('[data-price-amount]');
      const currencySpanSelector = '.price-currency';
      let currencySpan = finalPrice.querySelector(currencySpanSelector);

      if (!priceAmount) {
        priceAmount = document.createElement('span');
        priceAmount.className = 'product-price-final__amount';
        priceAmount.setAttribute('data-price-amount', 'true');
        if (currencySpan) {
          finalPrice.insertBefore(priceAmount, currencySpan);
        } else {
          finalPrice.appendChild(priceAmount);
        }
      }

      // Ensure currency span exists so we can keep spacing consistent
      if (!currencySpan) {
        currencySpan = document.createElement('span');
        currencySpan.className = 'price-currency';
        const currencyCode = finalPrice.dataset.priceCurrency || window.Shopify?.currency?.active || window.theme?.currency?.iso_code || '';
        if (currencyCode) {
          if (currencyCode !== 'AUD') currencySpan.textContent = currencyCode;
          currencySpan.classList.add(`price-currency--${currencyCode.toLowerCase()}`);
        }
        finalPrice.appendChild(currencySpan);
      } else {
        const currencyCode = finalPrice.dataset.priceCurrency || currencySpan.textContent.trim();
        if (currencyCode) {
          if (currencyCode !== 'AUD') currencySpan.textContent = currencyCode;
          currencySpan.classList.add(`price-currency--${currencyCode.toLowerCase()}`);
        }
      }

      // Update price amount text
      priceAmount.textContent = formatMoneyWithoutTrailingZeros(selectedVariant.price);

      // Update sale price styling
      if (selectedVariant.compare_at_price > selectedVariant.price) {
        finalPrice.classList.add('product-price-final-sale');
        if (hiddenSpan) {
          hiddenSpan.textContent = window.theme?.locales?.product?.sale_price || 'Sale price';
        }
      } else {
        finalPrice.classList.remove('product-price-final-sale');
        if (hiddenSpan) {
          hiddenSpan.remove();
        }
      }
    }

    // --- Handle compare price ---
    if (comparePrice) {
      let hiddenSpanCompare = comparePrice.querySelector('.visually-hidden');
      let compareTextNode = comparePrice.textContent.trim() ? 
        Array.from(comparePrice.childNodes).find(n => n.nodeType === Node.TEXT_NODE && n.textContent.trim() !== '') : 
        null;

      if (selectedVariant.compare_at_price > selectedVariant.price) {
        // Sale case - show compare price
        if (!hiddenSpanCompare) {
          hiddenSpanCompare = document.createElement('span');
          hiddenSpanCompare.className = 'visually-hidden';
          comparePrice.innerHTML = '';
          comparePrice.appendChild(hiddenSpanCompare);
          comparePrice.appendChild(document.createTextNode(''));
          compareTextNode = document.createTextNode('');
          comparePrice.appendChild(compareTextNode);
        }
        
        if (compareTextNode) {
          compareTextNode.textContent = formatMoneyWithoutTrailingZeros(selectedVariant.compare_at_price);
        }
        if (hiddenSpanCompare) {
          hiddenSpanCompare.textContent = window.theme?.locales?.product?.regular_price || 'Regular price';
        }
        comparePrice.removeAttribute('hidden');
      } else {
        // Regular price - hide compare price
        if (compareTextNode) compareTextNode.textContent = '';
        if (hiddenSpanCompare) {
          hiddenSpanCompare.textContent = window.theme?.locales?.product?.regular_price || 'Regular price';
        }
        comparePrice.setAttribute('hidden', 'hidden');
      }
    }

    // --- Handle compare badge ---
    if (compareBadge) {
      const discountPercentage = selectedVariant.compare_at_price > selectedVariant.price
        ? ((selectedVariant.compare_at_price - selectedVariant.price) / selectedVariant.compare_at_price) * 100
        : 0;
      const showPercentageBadge = compareBadge.dataset.discountType !== 'percentage' || discountPercentage >= 5;
      if (selectedVariant.compare_at_price > selectedVariant.price && showPercentageBadge) {
        const savings = compareBadge.dataset.discountType === 'percentage'
          ? `${Math.round(discountPercentage)}%`
          : formatMoneyWithoutTrailingZeros(selectedVariant.compare_at_price - selectedVariant.price);
        compareBadge.textContent = `SAVE ${savings}`;
        compareBadge.removeAttribute('hidden');
      } else {
        compareBadge.textContent = '';
        compareBadge.setAttribute('hidden', 'hidden');
      }
    }

    // --- Handle sold out badge ---
    if (soldOutBadge) {
      if (selectedVariant.available) {
        soldOutBadge.setAttribute('hidden', 'hidden');
      } else {
        soldOutBadge.removeAttribute('hidden');
      }
    }

    window.theme?.updateAfterpayProductPlacement?.(selectedVariant.price, this)
  }

  adjustButtons (selectedVariant) {
    const available = selectedVariant?.available
    const disabled = !selectedVariant || !available

    this.atcBtn.toggleAttribute('disabled', disabled)
    this.buyBtn?.toggleAttribute('disabled', disabled)
    if (this.cartAddUncertain) {
      this.atcBtn.textContent = 'Check cart';
      this.atcBtn.disabled = false;
      if (this.buyBtn) { this.buyBtn.textContent = 'Check cart'; this.buyBtn.disabled = false; }
      return
    }
    const atcLabel = selectedVariant
      ? (available ? window.theme.locales.product.add_to_cart : window.theme.locales.product.sold_out)
      : window.theme.locales.product.unavailable
    if (selectedVariant && available) {
      this.atcBtn.textContent = `${atcLabel} \u2022 ${formatMoneyWithoutTrailingZeros(selectedVariant.price)}`
    } else {
      this.atcBtn.textContent = atcLabel
    }
  }

  async onSubmit (event) {
    event.preventDefault()
    if (this.cartAddUncertain) {
      window.location.href = `${window.Shopify.routes.root}cart`;
      return
    }

    this.atcBtn.classList.add('loading')
    this.atcBtn.disabled = true
    this.atcBtn.setAttribute('aria-busy', 'true')

    try {
      const cartContainer = document.querySelector('cart-container')
      if (cartContainer) {
        const formData = new FormData(this.form)
        await cartContainer.add(formData)
      }
    } catch (error) {
      console.error('Error submitting add to cart:', error)
      this.cartAddUncertain = error.cartMayHaveChanged !== false
      this.atcBtn.textContent = this.cartAddUncertain ? 'Check cart' : 'Try again'
      if (this.cartAddUncertain && this.buyBtn) this.buyBtn.textContent = 'Check cart'
    } finally {
      this.atcBtn.classList.remove('loading')
      this.atcBtn.disabled = false
      this.atcBtn.setAttribute('aria-busy', 'false')
    }
  }

  async onClickBuyBtn (event) {
    event?.preventDefault()
    if (this.cartAddUncertain) {
      window.location.href = `${window.Shopify.routes.root}cart`;
      return
    }

    this.buyBtn.classList.add('loading')
    this.buyBtn.disabled = true
    this.buyBtn.setAttribute('aria-busy', 'true')

    try {
      const formData = new FormData(this.form)
      if (String(formData.get?.('id')) === '67705281806653') {
        await window.theme.checkSurpriseCartAddition(formData.get('id'))
        formData.set('quantity', '1')
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData
      })
      const data = await response.json()
      if (!response.ok) {
        const error = new Error(data.description || 'Failed to add item to cart')
        error.cartMayHaveChanged = response.status === 422 || response.status >= 500
        throw error
      }

      window.location.href = '/checkout'
    } catch (error) {
      console.error('Buy It Now failed:', error)
      this.cartAddUncertain = error.cartMayHaveChanged !== false
      this.buyBtn.textContent = this.cartAddUncertain ? 'Check cart' : 'Try again'
      if (this.cartAddUncertain) this.atcBtn.textContent = 'Check cart'
      document.querySelector('cart-container')?.showError(error.message || 'An error occurred while adding the item to your cart')
    } finally {
      this.buyBtn.classList.remove('loading')
      this.buyBtn.disabled = false
      this.buyBtn.setAttribute('aria-busy', 'false')
    }
  }
}
customElements.define('product-form', ProductForm)

class ProductMediaGallery extends HTMLElement {
  constructor () {
    super()

    this.initialSlide = Number(this.dataset.initialSlide)
    this.variantMedia = JSON.parse(this.dataset.variantMedia || '{}')
  }

  connectedCallback () {
    if (this.galleryConnected) return
    this.galleryConnected = true
    this.galleryGeneration = (this.galleryGeneration || 0) + 1

    this.initSwipers()
    this.setStickyPosition()
    this.listenVariantChange()
    this.listenMediaInteractions()
    this.listenProductTurn()
    this.updateProductTurnOnSlideChange(this.main)
    this.galleryVariantChangeHandler = (event) => {
      if (event.detail && typeof event.detail.position === 'number') {
        this.main?.slideTo(event.detail.position)
      }
    }
    this.addEventListener('variant:changed', this.galleryVariantChangeHandler)
  }

  disconnectedCallback () {
    this.galleryGeneration = (this.galleryGeneration || 0) + 1
    this.galleryConnected = false
    window.clearTimeout(this.gallerySwipeResetTimer)
    window.clearTimeout(this.galleryVideoResetTimer)
    this.gallerySwipeResetTimer = null
    this.galleryVideoResetTimer = null
    this.thumbnailContainer?.removeEventListener('click', this.thumbnailClickHandler)
    this.mainPreviousButton?.removeEventListener('click', this.mainPreviousClickHandler)
    this.mainNextButton?.removeEventListener('click', this.mainNextClickHandler)
    this.removeEventListener('pointerdown', this.turnPointerDownHandler, true)
    this.querySelectorAll('[data-product-turn]').forEach((turn) => {
      turn.removeEventListener('pointermove', this.turnPointerMoveHandler)
      turn.removeEventListener('pointerup', this.turnPointerEndHandler)
      turn.removeEventListener('pointercancel', this.turnPointerEndHandler)
      turn.removeEventListener('lostpointercapture', this.turnPointerEndHandler)
    })
    this.querySelectorAll('[data-turn-play]').forEach((button) => button.removeEventListener('click', this.turnPlayClickHandler))
    this.querySelectorAll('[data-turn-video]').forEach((video) => {
      video.removeEventListener('play', this.turnVideoPlayHandler)
      video.removeEventListener('pause', this.turnVideoPauseHandler)
      video.pause()
    })
    this.querySelectorAll('[data-product-turn]').forEach((turn) => turn.removeEventListener('keydown', this.turnKeydownHandler))
    this.removeEventListener('variant:changed', this.galleryVariantChangeHandler)
    window.removeEventListener('scroll', this.stickyScrollHandler, this.stickyScrollOptions)
    window.removeEventListener('kt.product.variant.change', this.windowVariantChangeHandler, this.windowVariantChangeOptions)
    this.main?.destroy(true, true)
    this.main = null
    this.resetSwipeTracking()
  }

  listenMediaInteractions () {
    this.thumbnailContainer = this.querySelector('.swiper-thumbs')
    this.thumbnailClickHandler = (event) => {
      const thumbnail = event.target.closest('[data-gallery-thumbnail]')
      if (thumbnail) {
        const thumbnailIndex = [...this.querySelectorAll('[data-gallery-thumbnail]')].indexOf(thumbnail)
        if (thumbnailIndex >= 0) {
          this.resetSwipeTracking()
          this.main?.slideTo(thumbnailIndex)
        }
        this.emitMediaInteraction('thumbnail_select', thumbnail, {
          input_method: event.detail === 0 ? 'keyboard' : 'pointer'
        })
      }
    }
    this.thumbnailContainer?.addEventListener('click', this.thumbnailClickHandler)

    this.mainPreviousButton = this.querySelector('.swiper-main .swiper-button-prev')
    this.mainPreviousClickHandler = (event) => {
      this.emitMediaInteraction('previous', null, {
        input_method: event.detail === 0 ? 'keyboard' : 'pointer'
      })
    }
    this.mainPreviousButton?.addEventListener('click', this.mainPreviousClickHandler)
    this.mainNextButton = this.querySelector('.swiper-main .swiper-button-next')
    this.mainNextClickHandler = (event) => {
      this.emitMediaInteraction('next', null, {
        input_method: event.detail === 0 ? 'keyboard' : 'pointer'
      })
    }
    this.mainNextButton?.addEventListener('click', this.mainNextClickHandler)
  }

  listenProductTurn () {
    this.turnPlayClickHandler = (event) => {
      const button = event.currentTarget
      const video = button.closest('[data-product-turn]')?.querySelector('[data-turn-video]')
      if (!video) return
      event.stopPropagation()
      if (video.paused) {
        delete video.dataset.turnStopped
        video.play().catch(() => {})
      } else {
        video.dataset.turnStopped = 'true'
        video.pause()
      }
    }
    this.turnKeydownHandler = (event) => {
      const turn = event.currentTarget
      if (event.target !== turn || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return
      const video = turn.querySelector('[data-turn-video]')
      if (!video || !Number.isFinite(video.duration) || video.duration <= 0) return
      event.preventDefault()
      video.dataset.turnStopped = 'true'
      video.pause()
      const frameDuration = 1 / 30
      const direction = event.key === 'ArrowRight' ? 1 : -1
      video.currentTime = (video.currentTime + direction * frameDuration + video.duration) % video.duration
    }
    this.turnVideoPlayHandler = (event) => {
      const video = event.currentTarget
      const button = video.closest('[data-product-turn]')?.querySelector('[data-turn-play]')
      if (button) {
        button.textContent = 'Pause'
        button.setAttribute('aria-label', `Pause ${this.dataset.productTitle || 'product'} 360° turn`)
      }
    }
    this.turnVideoPauseHandler = (event) => {
      const video = event.currentTarget
      const button = video.closest('[data-product-turn]')?.querySelector('[data-turn-play]')
      if (button) {
        button.textContent = 'Play'
        button.setAttribute('aria-label', `Play ${this.dataset.productTitle || 'product'} 360° turn`)
      }
    }
    this.turnPointerDownHandler = (event) => {
      const turn = event.target.closest?.('[data-product-turn]')
      if (!turn || event.target.closest?.('button, input') || event.button > 0 || this.turnPointer) return
      const video = turn.querySelector('[data-turn-video]')
      if (!video) return
      video.dataset.turnStopped = 'true'
      video.pause()
      this.turnPointer = { id: event.pointerId, x: event.clientX, time: video.currentTime || 0, video, turn }
      this.main.allowTouchMove = false
      turn.setPointerCapture?.(event.pointerId)
    }
    this.turnPointerMoveHandler = (event) => {
      const pointer = this.turnPointer
      if (!pointer || event.pointerId !== pointer.id || !Number.isFinite(pointer.video.duration)) return
      const duration = pointer.video.duration
      if (duration <= 0 || pointer.turn.clientWidth <= 0) return
      if (!pointer.tracked && Math.abs(event.clientX - pointer.x) >= 6) {
        pointer.tracked = true
        window.dispatchEvent(new CustomEvent('ws:track', {
          detail: { type: 'product_360_drag', payload: {
            product_handle: this.dataset.productHandle || null,
            product_title: this.dataset.productTitle || null,
            input_method: event.pointerType || 'pointer'
          } }
        }))
      }
      pointer.video.currentTime = ((pointer.time - (event.clientX - pointer.x) / pointer.turn.clientWidth * duration) % duration + duration) % duration
    }
    this.turnPointerEndHandler = (event) => {
      if (!this.turnPointer || event.pointerId !== this.turnPointer.id) return
      this.turnPointer = null
      if (this.main) this.main.allowTouchMove = true
    }
    this.addEventListener('pointerdown', this.turnPointerDownHandler, true)
    this.querySelectorAll('[data-product-turn]').forEach((turn) => {
      turn.addEventListener('pointermove', this.turnPointerMoveHandler)
      turn.addEventListener('pointerup', this.turnPointerEndHandler)
      turn.addEventListener('pointercancel', this.turnPointerEndHandler)
      turn.addEventListener('lostpointercapture', this.turnPointerEndHandler)
      turn.addEventListener('keydown', this.turnKeydownHandler)
      turn.querySelector('[data-turn-play]')?.addEventListener('click', this.turnPlayClickHandler)
      const video = turn.querySelector('[data-turn-video]')
      video?.addEventListener('play', this.turnVideoPlayHandler)
      video?.addEventListener('pause', this.turnVideoPauseHandler)
    })
  }

  updateProductTurnOnSlideChange (swiper = this.main) {
    const activeSlide = swiper?.slides?.[swiper.activeIndex] || this.querySelector('.swiper-slide-active')
    const activeVideo = activeSlide?.querySelector('[data-turn-video]')
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    this.querySelectorAll('[data-turn-video]').forEach((video) => {
      if (video !== activeVideo) video.pause()
    })
    if (activeVideo) {
      if (activeVideo.dataset.turnStopped !== 'true' && !reduceMotion) activeVideo.play().catch(() => {})
      else if (activeVideo.readyState === 0) activeVideo.load()
    }
  }

  emitMediaInteraction (action, media = null, details = {}) {
    window.dispatchEvent(new CustomEvent('ws:track', {
      detail: {
        type: 'product_gallery_interaction',
        payload: {
          action,
          product_id: this.dataset.productId || null,
          product_handle: this.dataset.productHandle || null,
          product_title: this.dataset.productTitle || null,
          media_id: media?.dataset.mediaId || null,
          media_position: media?.dataset.mediaPosition ? Number(media.dataset.mediaPosition) : null,
          media_type: media?.dataset.mediaType || null,
          ...details
        }
      }
    }))
  }

  getThumbnailForIndex (index) {
    return this.querySelectorAll('[data-gallery-thumbnail]')[index] || null
  }

  getNativeMediaSlideIndex (index) {
    const nativeIndex = Number(index)
    const turnSlideIndex = Number(this.querySelector('.product-gallery-turn-slide')?.dataset.mediaPosition)
    if (Number.isInteger(nativeIndex) && Number.isInteger(turnSlideIndex) && nativeIndex >= turnSlideIndex) {
      return nativeIndex + 1
    }
    return nativeIndex
  }

  updateThumbnailAccessibility (index = this.initialSlide) {
    this.querySelectorAll('.product-gallery-thumbnail').forEach((button, buttonIndex) => {
      const active = buttonIndex === index
      if (active) {
        button.setAttribute('aria-current', 'true')
      } else {
        button.removeAttribute('aria-current')
      }
      button.closest('[data-gallery-thumbnail]')?.classList.toggle('swiper-slide-thumb-active', active)
    })
  }

  startSwipeTracking (swiper, event) {
    window.clearTimeout(this.gallerySwipeResetTimer)
    this.gallerySwipeStartIndex = swiper.realIndex ?? swiper.activeIndex
    this.gallerySwipeMoved = false
    this.gallerySwipeInputMethod = event?.pointerType || (event?.type?.startsWith('touch') ? 'touch' : 'pointer')
  }

  trackSwipeIfNeeded (swiper) {
    const activeIndex = swiper.realIndex ?? swiper.activeIndex
    if (!this.gallerySwipeMoved || this.gallerySwipeStartIndex === activeIndex) return

    this.emitMediaInteraction('swipe', this.getThumbnailForIndex(activeIndex), {
      direction: swiper.swipeDirection || null,
      input_method: this.gallerySwipeInputMethod || 'touch'
    })
    this.resetSwipeTracking()
  }

  resetSwipeTracking () {
    window.clearTimeout(this.gallerySwipeResetTimer)
    this.gallerySwipeResetTimer = null
    this.gallerySwipeStartIndex = null
    this.gallerySwipeMoved = false
    this.gallerySwipeInputMethod = null
  }

  scheduleSwipeTrackingReset (swiper) {
    window.clearTimeout(this.gallerySwipeResetTimer)
    const transitionDuration = Number(swiper.params?.speed) || 0
    const generation = this.galleryGeneration
    this.gallerySwipeResetTimer = window.setTimeout(() => {
      if (!this.galleryConnected || generation !== this.galleryGeneration) return
      this.gallerySwipeResetTimer = null
      this.resetSwipeTracking()
    }, transitionDuration + 150)
  }

  
  initSwipers() {
    // ponytail: thumbnails are a plain CSS grid (every photo visible, no paging).
    // Clicks go through listenMediaInteractions; the active highlight is set in updateThumbnailAccessibility.
    this.main = new window.Swiper(this.querySelector('.swiper-main'), this.mainOptions());
    
    // Go to initial slide
    if (this.initialSlide) {
      this.main.slideTo(this.getNativeMediaSlideIndex(this.initialSlide));
    }
    this.updateThumbnailAccessibility(this.main.realIndex ?? this.main.activeIndex ?? this.initialSlide)
  }

  mainOptions () {
    /** @type {SwiperOptions} */
    const options = {
      speed: Number(this.dataset.speed),
      rewind: true,
      pagination: {
        type: 'bullets',
        el: '.swiper-pagination',
        dynamicBullets: true,
        dynamicMainBullets: 2,
        renderFraction: function (currentClass, totalClass) {
          return `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`
        }
      },
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev')
      },
      on: {
        slideChange: (swiper) => {
          this.resetVideoOnSlideChange()
          this.updateProductTurnOnSlideChange(swiper)
          const activeIndex = swiper.realIndex ?? swiper.activeIndex
          this.updateThumbnailAccessibility(activeIndex)
          this.trackSwipeIfNeeded(swiper)
        },
        touchStart: (swiper, event) => {
          if (event.target?.closest?.('[data-product-turn]')) {
            swiper.allowTouchMove = false
            return
          }
          this.startSwipeTracking(swiper, event)
        },
        sliderFirstMove: () => {
          this.gallerySwipeMoved = true
        },
        touchEnd: (swiper) => {
          if (this.turnPointer) {
            swiper.allowTouchMove = false
            return
          }
          swiper.allowTouchMove = true
          this.scheduleSwipeTrackingReset(swiper)
        }
      }
    }
    return options
  }

  resetVideoOnSlideChange() {
    // Small delay to ensure slide transition is complete
    window.clearTimeout(this.galleryVideoResetTimer)
    const generation = this.galleryGeneration
    this.galleryVideoResetTimer = window.setTimeout(() => {
      if (!this.galleryConnected || generation !== this.galleryGeneration) return
      this.galleryVideoResetTimer = null
      // Get the current active slide
      const activeSlide = this.querySelector('.swiper-slide-active')
      if (!activeSlide) return

      // Find video in active slide
      const video = activeSlide.querySelector('video')
      if (video && !video.hasAttribute('data-turn-video')) {
        // Reset video to beginning and ensure it plays
        video.currentTime = 0
        video.play().catch(() => {})
      }
    }, 50)
  }

  setStickyPosition () {
    if (this.closest('.product-main') && window.matchMedia('(min-width: 768px)').matches) {
      const headerGroup = document.querySelector('#header-group.sticky-top')
      const headerHeight = headerGroup?.clientHeight || 0

      this.galleryOldScroll = window.scrollY
      this.stickyScrollHandler = () => {
        const newScroll = window.scrollY
        if (newScroll > this.galleryOldScroll) {
          if (newScroll > headerHeight) {
            this.style.top = '20px'
          }
        } else if (newScroll < this.galleryOldScroll) {
          this.style.top = `${headerHeight + 20}px`
        }

        this.galleryOldScroll = Math.max(window.scrollY, 0)
      }
      this.stickyScrollOptions = { passive: true }
      window.addEventListener('scroll', this.stickyScrollHandler, this.stickyScrollOptions)
    }
  }

  listenVariantChange () {
    this.windowVariantChangeHandler = (event) => {
      const selectedVariant = event.detail

      if (selectedVariant.featured_media) {
        this.main?.slideTo(this.getNativeMediaSlideIndex(selectedVariant.featured_media.position - 1))
      }
    }
    this.windowVariantChangeOptions = false
    window.addEventListener('kt.product.variant.change', this.windowVariantChangeHandler, this.windowVariantChangeOptions)
  }
}
customElements.define('product-media-gallery', ProductMediaGallery)

// Shopify Subscriptions app
const adjustShopifySubscriptionsStyling = () => {
  document.querySelectorAll('.shopify_subscriptions_app_block [type="radio"]').forEach(radio => {
    radio.classList.add('form-check-input')
  })
}
adjustShopifySubscriptionsStyling()
window.addEventListener('kt.product.quick_view.modal_shown', adjustShopifySubscriptionsStyling)

// Product/collection link chips — upgrade internal links inside descriptions to inline chips with thumbnails
const initProductChips = async () => {
  const descriptions = document.querySelectorAll(
    '.description.rte, .collapse-inner.description.rte, .pdp-uplift-story__copy.rte'
  )
  if (!descriptions.length) return

  const productLinks = []
  const collectionLinks = []

  descriptions.forEach(desc => {
    desc.querySelectorAll('a[href*="/products/"]').forEach(link => {
      const match = link.href.match(/\/products\/([^?#]+)/)
      if (match) productLinks.push({ link, handle: match[1] })
    })
    desc.querySelectorAll('a[href*="/collections/"]').forEach(link => {
      const match = link.href.match(/\/collections\/([^?#]+)/)
      if (match) collectionLinks.push({ link, handle: match[1] })
    })
  })

  if (!productLinks.length && !collectionLinks.length) return

  // Fetch products via /products/[handle].js
  const productCache = {}
  const uniqueProductHandles = [...new Set(productLinks.map(p => p.handle))]
  await Promise.all(uniqueProductHandles.map(async handle => {
    try {
      const res = await fetch(`/products/${handle}.js`)
      if (res.ok) productCache[handle] = await res.json()
    } catch {}
  }))

  // Fetch collections via first product image (/collections/[handle]/products.json?limit=1)
  const collectionCache = {}
  const uniqueCollectionHandles = [...new Set(collectionLinks.map(c => c.handle))]
  await Promise.all(uniqueCollectionHandles.map(async handle => {
    try {
      const res = await fetch(`/collections/${handle}/products.json?limit=1&fields=title,images`)
      if (res.ok) {
        const data = await res.json()
        const first = data.products && data.products[0]
        if (first) collectionCache[handle] = { title: first.title, featured_image: first.images[0] && first.images[0].src }
      }
    } catch {}
  }))

  const applyChip = (link, imageUrl, alt) => {
    if (!imageUrl) return
    const img = document.createElement('img')
    img.src = imageUrl.replace(/(\.\w+)(\?.*)?$/, '_60x60$1$2')
    img.alt = alt
    img.width = 22
    img.height = 22
    img.className = 'product-chip__img'
    img.loading = 'lazy'
    link.classList.add('product-chip')
    link.prepend(img)
  }

  productLinks.forEach(({ link, handle }) => {
    const product = productCache[handle]
    if (product) applyChip(link, product.featured_image, product.title)
  })

  collectionLinks.forEach(({ link, handle }) => {
    const collection = collectionCache[handle]
    if (collection) applyChip(link, collection.featured_image, collection.title)
  })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductChips)
} else {
  initProductChips()
}
